// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { AgentExecutionForestView } from '@/components/remote/agents/AgentExecutionForestView';
import { AgentWorkForest } from '@/components/remote/agents/AgentWorkForest';
import {
  FinalAnswerBlock,
  LiveActivityBar,
  TurnHeader,
  useLiveDuration,
  WorkItemRenderer,
} from '@/components/remote/chat';
import {
  durationToDateTime,
  formatDuration,
  isActiveWorkStatus,
} from '@/components/remote/chat/presentation';
import { DisclosureTrigger } from '@/components/remote/primitives/Disclosure';
import { RemoteMarkdown } from '@/components/remote/RemoteMarkdown';
import { TimelineStep } from '@/components/remote/timeline/TimelineStep';
import { Button } from '@/components/ui/button';
import { MorphIcon } from '@/components/ui/morph-icon';
import type {
  ProjectedArtifact,
  ProjectedArtifactManifest,
} from '@web/lib/projector';
import type { ActivityItem, ActivityRun } from '@web/lib/remoteActivityModel';
import type { AgentExecutionForest } from '@web/lib/remoteAgentProjection';
import type {
  HumanInputItem,
  WorkItemStatus,
} from '@web/lib/remoteWorkItemModel';
import {
  Bot,
  Check,
  Copy,
  Download,
  FileText,
  ThumbsDown,
  ThumbsUp,
  Wrench,
} from 'lucide';
import type { IconInput } from 'morphicons/react';
import { useCallback, useId, useState } from 'react';
import { toast } from 'sonner';

const COPIED_RESET_MS = 2000;

export function artifactDisplayCount(
  artifacts: readonly ProjectedArtifact[],
  manifest?: ProjectedArtifactManifest
): number {
  return manifest?.artifactCount ?? artifacts.length;
}

function workItemStatus(run: ActivityRun): WorkItemStatus {
  switch (run.status) {
    case 'running':
      return 'in_progress';
    case 'waiting_input':
      return 'waiting';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'cancelled':
    case 'interrupted':
      return 'interrupted';
  }
}

type StepIndicator = {
  id: 'agent' | 'tool';
  icon: IconInput;
  label: string;
};

function RunStepIndicators({
  items,
  forest,
}: {
  items: ActivityItem[];
  forest: AgentExecutionForest;
}) {
  const indicators: StepIndicator[] = [];

  if (
    items.some((item) => item.kind === 'agent') ||
    Object.keys(forest.nodesById).length > 0
  ) {
    indicators.push({ id: 'agent', icon: Bot, label: 'Agent' });
  }

  const toolItem = items.find((item) => item.kind === 'tool');
  const projectedTool = Object.values(forest.toolsById)[0];
  if (toolItem || projectedTool) {
    const toolName = toolItem
      ? `${toolItem.title} ${toolItem.detail}`.toLowerCase()
      : projectedTool.name.toLowerCase();
    indicators.push({
      id: 'tool',
      icon: /document|file|pdf/.test(toolName) ? FileText : Wrench,
      label: /document|file|pdf/.test(toolName) ? 'Documents' : 'Tool',
    });
  }

  const projectedEventIds = new Set(
    Object.values(forest.nodesById).flatMap((node) => node.eventIds)
  );
  const stepCount = Math.max(items.length, projectedEventIds.size);
  const stepsLabel = `${stepCount} step${stepCount === 1 ? '' : 's'}`;

  return (
    <div
      className="flex min-w-0 items-center gap-2"
      aria-label={`${indicators.map((indicator) => indicator.label).join(', ') || 'No step types'} · ${stepsLabel}`}
    >
      {indicators.map((indicator) => (
        <MorphIcon
          key={indicator.id}
          icon={indicator.icon}
          className="h-4 w-4 shrink-0 text-ds-icon-neutral-muted-default"
          label={indicator.label}
        />
      ))}
      <span className="text-body-sm font-medium text-ds-text-neutral-default-default">
        {stepsLabel}
      </span>
    </div>
  );
}

function UserMessageBubble({ item }: { item: ActivityItem }) {
  return (
    <div className="flex justify-end pl-12 sm:pl-24">
      <div className="max-w-[85%] rounded-2xl rounded-br-md bg-ds-bg-neutral-default-default px-4 py-2.5">
        <p className="m-0 whitespace-pre-wrap break-words text-body-sm text-ds-text-neutral-default-default">
          {item.detail || item.title}
        </p>
      </div>
    </div>
  );
}

function ResultActions({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success('Copied to clipboard');
      window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  }, [content]);

  return (
    <div className="mt-2 flex items-center gap-1">
      <Button
        variant="ghost"
        size="xs"
        buttonContent="icon-only"
        buttonRadius="full"
        aria-label="Copy result"
        onClick={copy}
      >
        <MorphIcon icon={copied ? Check : Copy} />
      </Button>
      <Button
        variant="ghost"
        size="xs"
        buttonContent="icon-only"
        buttonRadius="full"
        aria-label="Helpful result"
        onClick={() => toast.success('Thanks for your feedback!')}
      >
        <MorphIcon icon={ThumbsUp} />
      </Button>
      <Button
        variant="ghost"
        size="xs"
        buttonContent="icon-only"
        buttonRadius="full"
        aria-label="Unhelpful result"
        onClick={() => toast.success('Thanks for your feedback!')}
      >
        <MorphIcon icon={ThumbsDown} />
      </Button>
    </div>
  );
}

function RunResult({ item }: { item: ActivityItem }) {
  return (
    <div className="w-full px-1 py-1">
      {item.detail ? (
        <>
          <RemoteMarkdown content={item.detail} />
          <ResultActions content={item.detail} />
        </>
      ) : (
        <p className="m-0 text-body-sm text-ds-text-neutral-muted-default">
          {item.state === 'failed'
            ? 'The task ended with an error.'
            : 'The task is complete.'}
        </p>
      )}
    </div>
  );
}

export function RunSection({
  run,
  current,
  defaultExpanded = false,
  onAnswerHumanInput,
  answeredHumanInteractionIds,
  artifacts = [],
  artifactManifest,
  onOpenArtifact,
}: {
  run: ActivityRun;
  current: boolean;
  defaultExpanded?: boolean;
  onAnswerHumanInput?: (item: HumanInputItem) => void;
  answeredHumanInteractionIds?: ReadonlySet<string>;
  artifacts?: ProjectedArtifact[];
  artifactManifest?: ProjectedArtifactManifest;
  onOpenArtifact?: (artifact: ProjectedArtifact) => void;
}) {
  const taskActive =
    current && (run.status === 'running' || run.status === 'waiting_input');
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [expandedDetailKeys, setExpandedDetailKeys] = useState<Set<string>>(
    () => new Set()
  );
  const setDetailExpanded = useCallback((key: string, next: boolean) => {
    setExpandedDetailKeys((current) => {
      const updated = new Set(current);
      if (next) updated.add(key);
      else updated.delete(key);
      return updated;
    });
  }, []);
  const disclosureId = `run-steps-${useId().replace(/:/g, '')}`;
  const normalized = Boolean(run.normalizedTurn);
  const status = workItemStatus(run);
  const elapsedMs = useLiveDuration({
    status,
    startedAt: run.startedAt,
    completedAt: run.completedAt,
    durationMs: run.durationMs,
  });
  const durationLabel = formatDuration(elapsedMs);
  const runtimeLabel = `${isActiveWorkStatus(status) ? 'Working' : 'Worked'} for ${durationLabel}`;
  const latestLegacyItem = [...run.items]
    .reverse()
    .find((item) => item.state === 'active' || item.state === 'waiting');
  const currentSummary =
    run.currentSummary ||
    latestLegacyItem?.detail ||
    latestLegacyItem?.title ||
    null;
  return (
    <article role="listitem" className="flex min-w-0 flex-col gap-3 py-2">
      {run.userIntent && <UserMessageBubble item={run.userIntent} />}

      {!normalized && (
        <TurnHeader
          status={status}
          startedAt={run.startedAt}
          completedAt={run.completedAt}
          durationMs={run.durationMs}
          activeAgentCount={run.activeAgentCount}
          itemCount={run.items.length}
          className="px-1"
        />
      )}

      {!normalized && (
        <LiveActivityBar
          status={status}
          summary={currentSummary}
          agentName={run.currentAgentName}
          activeAgentCount={run.activeAgentCount}
        />
      )}

      {normalized ? (
        <section className="min-w-0">
          <DisclosureTrigger
            expanded={expanded}
            onExpandedChange={setExpanded}
            expandedChevron="down"
            chevronPlacement="inline"
            ariaLabel={`${expanded ? 'Collapse' : 'Expand'} ${run.title} work details · ${runtimeLabel}`}
            controlsId={disclosureId}
            className="min-h-11 gap-1.5 border-x-0 border-b border-l-0 border-r-0 border-t-0 border-solid border-ds-border-neutral-subtle-default px-1 py-2.5 hover:text-ds-text-neutral-default-hover"
          >
            <span className="text-body-sm text-ds-text-neutral-muted-default">
              {isActiveWorkStatus(status) ? 'Working' : 'Worked'} for{' '}
              <time
                dateTime={durationToDateTime(elapsedMs)}
                aria-label={
                  elapsedMs == null
                    ? 'Runtime unavailable'
                    : `Runtime ${durationLabel}`
                }
                className="tabular-nums"
              >
                {durationLabel}
              </time>
            </span>
          </DisclosureTrigger>

          {expanded && (
            <div id={disclosureId} className="px-0 py-3">
              <AgentWorkForest
                rootExecutionKeys={run.rootExecutionKeys}
                agentsById={run.agentExecutionsById}
                itemsById={run.typedWorkItemsById}
                mode={run.mode}
                turnFinalAnswerItemKey={run.normalizedTurn?.finalAnswerItemKey}
                expandedDetailKeys={expandedDetailKeys}
                onDetailExpandedChange={setDetailExpanded}
              />
            </div>
          )}
        </section>
      ) : (
        <section className="overflow-hidden rounded-xl border border-solid border-ds-border-neutral-subtle-default bg-ds-bg-neutral-default-default">
          <DisclosureTrigger
            expanded={expanded}
            onExpandedChange={setExpanded}
            ariaLabel={`${expanded ? 'Collapse' : 'Expand'} ${run.title} work details`}
            controlsId={disclosureId}
            className="min-h-12 px-4 py-3 hover:bg-ds-bg-neutral-subtle-hover"
          >
            <RunStepIndicators items={run.items} forest={run.agentForest} />
          </DisclosureTrigger>

          {expanded && (
            <div
              id={disclosureId}
              className="border-x-0 border-b-0 border-t border-solid border-ds-border-neutral-subtle-default px-2 py-2"
            >
              {run.items.length > 0 || run.agentForest.rootIds.length > 0 ? (
                <>
                  <AgentExecutionForestView
                    forest={run.agentForest}
                    items={run.items}
                  />
                  {run.runLevelItems.length > 0 && (
                    <ol
                      className="m-0 list-none p-0"
                      aria-label={`${run.title} run-level steps`}
                    >
                      {run.runLevelItems.map((item, index) => (
                        <TimelineStep
                          key={item.id}
                          item={item}
                          last={index === run.runLevelItems.length - 1}
                        />
                      ))}
                    </ol>
                  )}
                </>
              ) : (
                <p className="m-0 text-body-sm text-ds-text-neutral-muted-default">
                  {taskActive
                    ? 'Waiting for task activity…'
                    : 'No intermediate task steps were recorded.'}
                </p>
              )}
            </div>
          )}
        </section>
      )}

      {run.unsupportedWorkItems.map((item) => (
        <WorkItemRenderer key={item.key} item={item} />
      ))}

      {run.pendingHumanInputs
        .filter((item) => !answeredHumanInteractionIds?.has(item.interactionId))
        .map((item) => (
          <WorkItemRenderer
            key={item.key}
            item={item}
            onAnswerHumanInput={onAnswerHumanInput}
          />
        ))}

      {run.finalAnswer ? (
        <FinalAnswerBlock item={run.finalAnswer} />
      ) : (
        run.result && <RunResult item={run.result} />
      )}

      {(artifacts.length > 0 ||
        artifactManifest?.truncated ||
        (artifactManifest?.scanStatus &&
          artifactManifest.scanStatus !== 'complete')) && (
        <section className="overflow-hidden rounded-xl border border-solid border-ds-border-neutral-subtle-default bg-ds-bg-neutral-default-default">
          <div className="border-x-0 border-b border-l-0 border-r-0 border-t-0 border-solid border-ds-border-neutral-subtle-default px-4 py-3">
            <p className="m-0 text-body-sm font-semibold text-ds-text-neutral-default-default">
              Files changed ·{' '}
              {artifactDisplayCount(artifacts, artifactManifest)}
            </p>
          </div>
          {artifactManifest?.truncated ||
          (artifactManifest?.scanStatus &&
            artifactManifest.scanStatus !== 'complete') ? (
            <p className="m-0 border-x-0 border-b border-l-0 border-r-0 border-t-0 border-solid border-ds-border-neutral-subtle-default bg-ds-bg-warning-subtle-default px-4 py-2 text-body-xs text-ds-text-warning-default-default">
              {artifactManifest.truncated
                ? 'This is a partial durable manifest because the bounded file scan reached its limit.'
                : `File discovery completed with status: ${artifactManifest.scanStatus}.`}
            </p>
          ) : null}
          <ul className="m-0 grid list-none gap-0 p-0 sm:grid-cols-2">
            {artifacts.map((artifact) => (
              <li
                key={artifact.artifactId}
                className="min-w-0 border-x-0 border-b border-l-0 border-r-0 border-t-0 border-solid border-ds-border-neutral-subtle-default px-4 py-3"
              >
                <button
                  type="button"
                  disabled={!artifact.assetRef?.chatFileId || !onOpenArtifact}
                  onClick={() => onOpenArtifact?.(artifact)}
                  className="flex w-full min-w-0 items-start gap-2 border-0 bg-transparent p-0 text-left disabled:cursor-default"
                  aria-label={
                    artifact.assetRef?.chatFileId
                      ? `Open ${artifact.name}`
                      : `${artifact.name} is not uploaded yet`
                  }
                >
                  <MorphIcon
                    icon={FileText}
                    className="mt-0.5 h-4 w-4 shrink-0 text-ds-icon-neutral-muted-default"
                  />
                  <div className="min-w-0">
                    <p className="m-0 truncate text-body-sm font-medium text-ds-text-neutral-default-default">
                      {artifact.name}
                    </p>
                    <p
                      className="m-0 truncate text-body-xs text-ds-text-neutral-muted-default"
                      title={artifact.relativePath}
                    >
                      {artifact.relativePath}
                    </p>
                  </div>
                  {artifact.assetRef?.chatFileId ? (
                    <MorphIcon
                      icon={Download}
                      className="mt-0.5 h-4 w-4 shrink-0 text-ds-icon-neutral-muted-default"
                    />
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}
